import dayjs from "dayjs";

// THE PURE RULES OF `pillars-assist.component.ts` / `pillars-assist.service.ts`, KEPT OUT OF THE
// COMPONENT SO THE SPEC'S CASES CAN PIN THEM.

// ONE PLAYER THE AI SEARCH FOUND (`resp.data[]` OF `/pillars-assist/send-prompt`)
export interface AssistPatron {
  name?: string;
  patronId?: number;
  cardId?: string;
  birthdate?: string;
  location?: string;
  status?: string;
  sessionId?: number;
  [serverField: string]: unknown;
}

// ONE EXCHANGE IN THE CONVERSATION HISTORY. `request` IS WHAT `SendPrompt` POSTS: ANGULAR POSTS
// THE HISTORY ITEM ITSELF, SO THE BODY IS `{ isThinking: true, prompt, response: "" }`
export interface HistoryItem {
  id: number;
  prompt: string;
  response: string;
  isThinking: boolean;
  patrons?: AssistPatron[];
  selectedPatron?: number;
  isLoading?: boolean;
}

// `GetPatronDetail`'S `resp.data`, AS FAR AS THE TEMPLATES READ IT
export interface PatronDetail {
  name?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: string;
  birthdate?: string;
  enrollDate?: string;
  email?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  Player?: {
    cmsPlayerId?: string;
    Host?: { hostName?: string } | null;
    PlayerCard?: { PlayerCardLevel?: { description?: string } | null } | null;
  } | null;
  [serverField: string]: unknown;
}

// `currentPatron`: THE DETAIL, WITH THE SEARCH RESULT'S IDS AND LOCATION LAID OVER IT
export interface CurrentPatron extends PatronDetail {
  patronId?: number;
  cardId?: string;
  location?: string;
  cmsPlayerId?: string;
  status?: string;
  sessionId?: number;
  hostName?: string;
}

// `CapitalizeName`: TRIMMED, SPLIT ON SINGLE SPACES, EACH WORD UPPER-CASE FIRST, LOWER-CASE REST
export function capitalizeName(name: string | null | undefined): string {
  if (!name || name.trim() === "") return "";
  return name
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

// THE TRANSCRIPT SUBSCRIPTION: A SPACE BETWEEN, UNLESS THE PROMPT IS EMPTY OR ALREADY ENDS IN ONE
export function appendTranscript(prompt: string, transcript: string): string {
  const separator = prompt && !prompt.endsWith(" ") ? " " : "";
  return prompt + separator + transcript;
}

// `onPromptKeydown`: ENTER SUBMITS, SHIFT+ENTER ADDS A NEW LINE, ANYTHING ELSE IS LEFT ALONE
export function keydownAction(key: string, shiftKey: boolean): "submit" | "newline" | null {
  if (key !== "Enter") return null;
  return shiftKey ? "newline" : "submit";
}

// `submitPrompt()`'S GUARDS, IN ORDER. THIS IS THE ENFORCEMENT POINT, NOT THE BUTTON'S
// `disabled`: THE ENTER KEY REACHES IT WITHOUT CONSULTING THE TEMPLATE
export function submitDecision(s: { aiAvailable: boolean; prompt: string; dictationBusy: boolean }): "ignore" | "dictating" | "submit" {
  if (!s.aiAvailable || !s.prompt) return "ignore";
  if (s.dictationBusy) return "dictating";
  return "submit";
}

export const DICTATING_TOAST = {
  title: "Dictation",
  detail: "Dictation is still in progress. Stop it to include what you said, or cancel it, then send.",
};

// THE SEND BUTTON'S `[disabled]`
export const sendDisabled = (s: { prompt: string; aiAvailable: boolean; dictationBusy: boolean }) => !s.prompt || !s.aiAvailable || s.dictationBusy;

// A SUBMIT: THE OLDEST ITEM STOPS "THINKING" IF IT STILL WAS (ANGULAR CHECKS
// `promptHistory[length - 1]`, WHICH, WITH `unshift`, IS THE OLDEST), AND THE NEW PROMPT GOES FIRST
export function pushPrompt(history: readonly HistoryItem[], prompt: string, id: number): HistoryItem[] {
  const next = history.map((h, i) => (i === history.length - 1 && h.isThinking ? { ...h, isThinking: false } : h));
  return [{ id, isThinking: true, prompt, response: "" }, ...next];
}

// THE POST BODY `SendPrompt` SENDS FOR A HISTORY ITEM (`processPrompt` SETS `isThinking` FIRST)
export const promptRequest = (item: HistoryItem) => ({ isThinking: true, prompt: item.prompt, response: item.response });

// THE "success" CASE: NO BODY OR NO `data` IS "NO PLAYER FOUND."; AN ARRAY OF PLAYERS IS
// NAME-CAPITALIZED AND OFFERED IN THE DROPDOWN; THE COUNT LINE IS ANGULAR'S, "players(s)" AND ALL
export function promptResult(resp: unknown): { response: string; patrons?: AssistPatron[] } {
  const data = resp && typeof resp === "object" ? (resp as { data?: unknown }).data : undefined;
  if (!data) return { response: "No player found." };
  if (Array.isArray(data)) {
    const patrons = (data as AssistPatron[]).map((p) => ({ ...p, name: capitalizeName(p?.name) }));
    return { response: "Found: " + patrons.length + " players(s).", patrons };
  }
  // A NON-ARRAY `data` READS ITS `length`, AS ANGULAR'S STRING CONCATENATION DOES
  const length = (data as { length?: unknown }).length;
  return { response: "Found: " + String(length) + " players(s)." };
}

export const ERROR_RESPONSE = "An error occurred while processing the prompt.";
export const UNAVAILABLE_RESPONSE = "Pillars Assist is temporarily unavailable. It will reconnect automatically when AI is back online.";

// THE HEALTH PROBE: ONLY AN EXPLICIT `healthy: false` MEANS DOWN. AN UNRECOGNIZED 200 BODY IS
// REACHABLE, SO A SHAPE CHANGE CANNOT STRAND THE POLL "UNAVAILABLE" (A 503 STILL THROWS)
export function assertHealthy(resp: unknown): void {
  const r = resp && typeof resp === "object" ? (resp as { data?: { healthy?: unknown } | null; healthy?: unknown }) : undefined;
  const healthy = r?.data?.healthy ?? r?.healthy;
  if (healthy === false) throw new Error("PILLARS ASSIST AI STILL UNAVAILABLE");
}

// `getPatronDetail`: THE DETAIL, WITH THE SEARCH RESULT'S IDS, LOCATION, STATUS AND SESSION, AND
// THE PLAYER'S CMS ID AND HOST NAME HOISTED. NULL WHEN THE RESPONSE HAS NO `data`
export function mergePatronDetail(resp: unknown, patron: AssistPatron): CurrentPatron | null {
  const data = resp && typeof resp === "object" ? (resp as { data?: PatronDetail | null }).data : undefined;
  if (!data) return null;
  return {
    ...data,
    patronId: patron.patronId,
    cardId: patron.cardId,
    location: patron.location,
    cmsPlayerId: data.Player?.cmsPlayerId,
    status: patron.status,
    sessionId: patron.sessionId,
    hostName: data.Player?.Host?.hostName,
  };
}

// `getSession`'S FIRST GUARD: A PATRON WITH NO SESSION OR NO LOCATION HAS NOTHING TO SHOW
export const hasSession = (p: CurrentPatron | null | undefined): p is CurrentPatron & { sessionId: number; location: string } => !!p && !!p.sessionId && !!p.location;

// THE `get-active-session` BODY
export const sessionRequest = (p: { sessionId?: number; location?: string }) => ({ session_id: p.sessionId, location: p.location });

// PIPES ------------------------------------------------------------------------

// `uppercase`: NULL AND UNDEFINED RENDER AS NOTHING
export const upper = (v: unknown) => (v === null || v === undefined ? "" : String(v).toUpperCase());

// `titlecase`: EVERY RUN OF LETTERS / DIGITS UPPER-CASE FIRST, LOWER-CASE REST ("o'BRIEN" -> "O'Brien")
export function titleCase(v: string | null | undefined): string {
  if (!v) return "";
  return v.replace(/[\p{L}\p{N}]+/gu, (w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase());
}

// `date` (DEFAULT AND "mediumDate"): "Sep 28, 2026"; EMPTY FOR A MISSING VALUE
export const mediumDate = (v: string | null | undefined) => (v ? dayjs(v).format("MMM D, YYYY") : "");

// `age`: WHOLE YEARS SINCE THE DATE (`moment().diff(value, "years")`)
export const age = (v: string | null | undefined, now: Date = new Date()) => dayjs(now).diff(dayjs(v ?? undefined), "year");

// THE DETAIL / LOCATION `p-tag`: "info" FOR "female", OTHERWISE PRIMARY
export const genderSeverity = (gender: string | undefined) => (gender === "female" ? "info" : "primary");
